import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Zap, CalendarClock } from "lucide-react";

export default function AdminReminders() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("due");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reminders", { params: { scope } });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (item) => {
    try {
      await api.patch(`/admin/leads/${item.lead_id}/reminders/${item.id}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const overdue = items.filter((i) => i.overdue).length;

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Rappels du jour</h1>
        <p className="mt-2 text-neutral-500">
          Tout ce qu'il faut faire aujourd'hui, tous élèves confondus.
          {overdue > 0 && (
            <strong className="ml-1 text-red-600" data-testid="overdue-count">
              {overdue} en retard.
            </strong>
          )}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {[
            ["due", "À faire aujourd'hui"],
            ["open", "Tous les rappels ouverts"],
            ["all", "Tout (fait inclus)"],
          ].map(([value, label]) => (
            <button
              key={value}
              onClick={() => setScope(value)}
              data-testid={`filter-reminders-${value}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                scope === value ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <ul className="mt-5 space-y-2" data-testid="reminders-board">
          {items.length === 0 && (
            <li className="rounded-lg border border-dashed border-neutral-300 px-5 py-8 text-sm text-neutral-500" data-testid="no-reminders">
              Rien à faire pour l'instant.
            </li>
          )}
          {items.map((i) => (
            <li
              key={`${i.lead_id}-${i.id}`}
              data-testid={`reminder-item-${i.id}`}
              className={`flex flex-wrap items-center gap-3 rounded-lg border bg-white px-4 py-3 ${
                i.overdue ? "border-red-200 bg-red-50/50" : "border-neutral-200"
              }`}
            >
              <input
                type="checkbox"
                checked={i.done}
                onChange={() => toggle(i)}
                data-testid={`board-toggle-${i.id}`}
                className="h-4 w-4 accent-brand"
              />
              <span className={`font-semibold ${i.done ? "line-through text-neutral-400" : ""}`}>{i.text}</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                <CalendarClock className="h-3.5 w-3.5" />
                {i.due_date || "sans échéance"}
                {i.overdue && <span className="font-bold text-red-600">en retard</span>}
              </span>
              <Link
                to={`/admin/leads/${i.lead_id}`}
                data-testid={`reminder-lead-link-${i.id}`}
                className="ml-auto inline-flex items-center gap-2 text-sm font-semibold hover:text-brand transition-colors"
              >
                {i.lead_name}
                {i.urgent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700">
                    <Zap className="h-3 w-3" /> Urgent
                  </span>
                )}
              </Link>
              <span className="text-xs text-neutral-500">{i.school_name || "non attribué"}</span>
              <StatusBadge status={i.lead_status} testId={`reminder-status-${i.id}`} />
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}
